import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import {
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  Video,
  FileText,
  MessageSquare,
  Network,
  Database,
  Zap
} from 'lucide-react';

// Sub-step progress from video_outline stage
interface SubStepProgress {
  stage: string;
  sub_step: string;
  display_name: string;
  description: string;
  status: 'in_progress' | 'completed';
  progress: number;
  cumulative_progress: number;
  updated_at: string;
}

// Phase 3.5: Video detail modal for stage-by-stage progress visualization
interface VideoDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoData: {
    content_id: string;
    slug: string;
    title: string;
    content_type: 'article' | 'video';
    processing_status: 'pending' | 'processing' | 'completed' | 'failed';
    url: string;
    current_stage?: string;
    progress_percentage?: number;
    stages_completed?: number;
    stage_statuses?: Record<string, string>;
    updated_at?: string | null;
    video_id?: number | null;
    current_sub_step?: SubStepProgress;
  } | null;
}

// Video pipeline stages configuration
const VIDEO_STAGES = [
  {
    id: 'video_fetch',
    name: 'Video Fetch',
    description: 'Fetching video metadata and information',
    icon: Video,
  },
  {
    id: 'video_transcript',
    name: 'Transcript',
    description: 'Extracting and processing video transcript',
    icon: FileText,
  },
  {
    id: 'video_outline',
    name: 'Outline',
    description: 'Generating structured outline from content',
    icon: MessageSquare,
  },
  {
    id: 'video_entities',
    name: 'Entities',
    description: 'Extracting entities and key concepts',
    icon: Database,
  },
  {
    id: 'video_relationships',
    name: 'Relationships',
    description: 'Analyzing relationships between entities',
    icon: Network,
  },
  {
    id: 'video_vectors',
    name: 'Vectors',
    description: 'Generating vector embeddings for search',
    icon: Zap,
  },
];

export function VideoDetailModal({ open, onOpenChange, videoData }: VideoDetailModalProps) {
  // Only render for video content
  if (!videoData || videoData.content_type !== 'video') {
    return null;
  }

  // Get status icon for a stage
  const getStageStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  // Get status badge variant
  const getStageStatusBadge = (status: string | undefined) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-500">Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'processing':
        return <Badge variant="default" className="bg-blue-500">Processing</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      default:
        return <Badge variant="outline">Not Started</Badge>;
    }
  };

  // Calculate stage progress percentage
  const getStageProgress = (stageId: string): number => {
    if (!videoData.stage_statuses) return 0;
    const status = videoData.stage_statuses[stageId];
    if (status === 'completed') return 100;
    if (status === 'failed') return 100; // Show full bar for failed

    // For video_outline stage, use cumulative_progress from sub-steps if available
    if (stageId === 'video_outline' && status === 'processing' && videoData.current_sub_step) {
      return videoData.current_sub_step.cumulative_progress * 100;
    }

    if (status === 'processing') return 50;
    return 0;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Video Processing Details
          </DialogTitle>
          <DialogDescription>
            Stage-by-stage progress for video content processing
          </DialogDescription>
        </DialogHeader>

        {/* Video Information */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="font-medium text-lg">{videoData.title}</div>
            <div className="text-sm text-muted-foreground">{videoData.slug}</div>
            <div className="text-xs text-muted-foreground font-mono">
              ID: {videoData.content_id.substring(0, 8)}...
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                <Video className="h-3 w-3 mr-1 inline" />
                Video
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {videoData.processing_status}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Overall Progress */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className="text-sm text-muted-foreground">
                {videoData.stages_completed || 0}/6 stages completed
              </span>
            </div>
            <Progress value={videoData.progress_percentage || 0} />
            <div className="text-xs text-muted-foreground text-right">
              {(videoData.progress_percentage || 0).toFixed(1)}%
            </div>
          </CardContent>
        </Card>

        {/* Stage-by-Stage Progress */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Pipeline Stages</h3>
          {VIDEO_STAGES.map((stage, index) => {
            const status = videoData.stage_statuses?.[stage.id];
            const StageIcon = stage.icon;
            const isActive = videoData.current_stage === stage.id;

            return (
              <Card key={stage.id} className={isActive ? 'border-blue-500 border-2' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Stage Number and Icon */}
                    <div className="flex flex-col items-center gap-1">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
                        <span className="text-sm font-medium">{index + 1}</span>
                      </div>
                      <StageIcon className="h-4 w-4 text-muted-foreground" />
                    </div>

                    {/* Stage Details */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {stage.name}
                            {isActive && (
                              <Badge variant="outline" className="text-xs">
                                Current
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {stage.description}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStageStatusIcon(status)}
                          {getStageStatusBadge(status)}
                        </div>
                      </div>

                      {/* Stage Progress Bar */}
                      <Progress
                        value={getStageProgress(stage.id)}
                        className={status === 'failed' ? 'bg-red-100' : ''}
                      />

                      {/* Sub-step progress for video_outline stage */}
                      {stage.id === 'video_outline' && status === 'processing' && videoData.current_sub_step && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
                              <span className="text-sm font-medium text-blue-700">
                                {videoData.current_sub_step.display_name}
                              </span>
                            </div>
                            <span className="text-xs text-blue-600">
                              {(videoData.current_sub_step.cumulative_progress * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="text-xs text-blue-600 mb-2">
                            {videoData.current_sub_step.description}
                          </div>
                          <Progress
                            value={videoData.current_sub_step.cumulative_progress * 100}
                            className="h-1.5"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Timestamp */}
        {videoData.updated_at && (
          <div className="text-xs text-muted-foreground text-center">
            Last updated: {new Date(videoData.updated_at).toLocaleString()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
